// import axios from "axios";
// //import { API_BASE_URL } from "../Constants/UrlConstants";
// const { BASE_URL } = require("../utils/urls");
// // Create Axios instance
// const axiosInstance = axios.create({
//   baseURL: `${BASE_URL}`, 
// });

// axiosInstance.interceptors.request.use(
//   (config) => {
//     const token = localStorage.getItem("accessToken");
//     if (token) {
//       config.headers.Authorization = `Bearer ${token}`;
//     }
//     return config;
//   },
//   (error) => Promise.reject(error)
// );

// let isRefreshing = false;
// let failedQueue: { resolve: (value: unknown) => void; reject: (reason?: any) => void; }[] = [];

// const processQueue = (error: unknown, token = null) => {
//   failedQueue.forEach((prom) => {
//     if (error) {
//       prom.reject(error);
//     } else {
//       prom.resolve(token);
//     }
//   });
//   failedQueue = [];
// };

// axiosInstance.interceptors.response.use(
//   (response) => response,
//   async (error) => {
//     const originalRequest = error.config || {};

//     if (
//       error.response &&
//       error.response.status === 401 &&
//       !originalRequest._retry
//     ) {
//       originalRequest._retry = true;

//       if (isRefreshing) {
//         return new Promise(function (resolve, reject) {
//           failedQueue.push({ resolve, reject });
//         })
//           .then((token) => {
//             originalRequest.headers.Authorization = `Bearer ${token}`;
//             return axiosInstance(originalRequest);
//           })
//           .catch((err) => Promise.reject(err));
//       }
//       isRefreshing = true;
//       try {
//         const refreshToken = localStorage.getItem("refreshToken");
//         const res = await axios.post(`${BASE_URL}/api/auth/refresh-token`, {
//           refreshToken,
//         });
//         const newToken = res.data.accessToken;
//         const newRefreshToken = res.data.refreshToken;

//         localStorage.setItem("accessToken", newToken);
//         localStorage.setItem("refreshToken", newRefreshToken);

//         axiosInstance.defaults.headers.common[
//           "Authorization"
//         ] = `Bearer ${newToken}`;

//         processQueue(null, newToken);

//         if (!originalRequest.headers) originalRequest.headers = {};
//         originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
//         return axiosInstance(originalRequest);
//       } catch (err) {
//         processQueue(err, null);
//         localStorage.removeItem("accessToken");
//         localStorage.removeItem("refreshToken");
//         window.location.href = "/";
//         return Promise.reject(err);
//       } finally {
//         isRefreshing = false;
//       }
//     }

//     return Promise.reject(error);
//   }
// );

// export default axiosInstance;

import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { BASE_URL } from "../utils/urls"; // use ES import consistently
 
// ─── Axios instance ───────────────────────────────────────────────────────────
 
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});
 
// ─── Request interceptor — attach access token ────────────────────────────────
 
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("accessToken");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);
 
// ─── Refresh token queue ──────────────────────────────────────────────────────
 
let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject:  (err: unknown)  => void;
}[] = [];
 
/**
 * After a refresh attempt, resolve or reject all queued requests.
 */
const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token!)));
  failedQueue = [];
};
 
// ─── Response interceptor — handle 401, refresh, retry ───────────────────────
 
axiosInstance.interceptors.response.use(
  (response) => response, // pass through all 2xx
 
  async (error) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };
 
    // Only handle 401 Unauthorized, only once per request
    if (!error.response || error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }
 
    // Mark so we don't retry again for this request
    originalRequest._retry = true;
 
    // If a refresh is already in progress, queue this request
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((newToken) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return axiosInstance(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }
 
    // Start refresh
    isRefreshing = true;
 
    try {
      const refreshToken = localStorage.getItem("refreshToken");

      if (!refreshToken) {
        processQueue(new Error("No refresh token"), null);
        redirectToLogin();
        return Promise.reject(error);
      }
 
      const { data } = await axios.post(
        `${BASE_URL}/api/auth/refresh-token`,
        { refreshToken },
        { headers: { "Content-Type": "application/json" } }
      );
 
      const newAccessToken  = data.accessToken;
      const newRefreshToken = data.refreshToken; // rotating refresh token
 
      // ── Persist new tokens ──────────────────────────────────────────────
      localStorage.setItem("accessToken",  newAccessToken);
      localStorage.setItem("refreshToken", newRefreshToken);
 
      // Update default headers for future requests
      axiosInstance.defaults.headers.common["Authorization"] =
        `Bearer ${newAccessToken}`;
 
      // Resolve all queued requests with the new token
      processQueue(null, newAccessToken);
 
      // Retry the original request with the new token
      if (!originalRequest.headers) originalRequest.headers = {};
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return axiosInstance(originalRequest);
 
    } catch (refreshError: any) {
      // Refresh itself failed (expired, revoked, network error)
      processQueue(refreshError, null);
 
      // Clear everything and force re-login
      clearAuthAndRedirect();
      return Promise.reject(refreshError);
 
    } finally {
      isRefreshing = false;
    }
  }
);
 
// ─── Helpers ──────────────────────────────────────────────────────────────────
 
function redirectToLogin() {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  window.location.href = "/";
}
 
function clearAuthAndRedirect() {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("orgId");
  localStorage.removeItem("orgDetails");
  localStorage.removeItem("userId");
  window.location.href = "/?session=expired"; // optional query param for UI message
}
 
export default axiosInstance;
 